/* =========================================================================
   MALVI — Sticky Add To Cart (mobile only)
   Shows a sticky price + ATC bar as soon as the product page loads. Reuses the existing <add-to-cart> component for single
   variant products and smooth-scrolls to the picker for multi-variant ones.
   While visible it nudges bottom-anchored floating widgets (cookie / chat)
   up so they don't clash with the bar.
   ========================================================================= */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768px)';
  var HEADER_OFFSET = 90; // approx sticky-header height when scrolling to picker

  function init() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;

    var sectionId = bar.getAttribute('data-product-section');
    var section = sectionId ? document.getElementById(sectionId) : null;
    if (!section) return;

    var origForm = section.querySelector('add-to-cart');
    var origButton = origForm ? origForm.querySelector('button.add-to-cart') : null;
    if (!origButton) return; // product has no buyable button (empty / placeholder)

    var origPrice = section.querySelector('[data-product-price]');
    var origText = origButton.querySelector('[data-add-to-cart-text]');

    var stickyButton = bar.querySelector('[data-sticky-atc-button]');
    var stickyPrice = bar.querySelector('[data-sticky-atc-price]');
    var stickyText = bar.querySelector('[data-sticky-atc-text]');
    var multiVariant = bar.getAttribute('data-multi-variant') === 'true';

    // Move the bar to <body> so it escapes .transition-body's stacking context
    // (z-index + opacity animation) and can't be buried under other layers.
    if (bar.parentNode !== document.body) document.body.appendChild(bar);

    var mql = window.matchMedia(MOBILE_QUERY);

    /* ---- keep the clone in sync with the real button / price ---- */
    function sync() {
      if (origPrice && stickyPrice) stickyPrice.innerHTML = origPrice.innerHTML;
      if (origText && stickyText) stickyText.textContent = origText.textContent.trim();
      stickyButton.disabled = origButton.disabled;
      bar.classList.toggle('is-disabled', origButton.disabled);
    }
    sync();

    var mo = new MutationObserver(sync);
    if (origPrice) {
      mo.observe(origPrice, { childList: true, subtree: true, characterData: true });
    }
    mo.observe(origButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true
    });

    /* ---- quantity stepper: proxies the section's own quantity selector so
       MOQ stepping (theme.QtySelector) and stock checks (<add-to-cart>)
       keep working; the sticky input just mirrors it ---- */
    var stickyQty = bar.querySelector('[data-sticky-atc-qty-input]');
    var origQty = section.querySelector('.product__quantity .js-qty__num');

    if (stickyQty && origQty) {
      var origQtyWrap = origQty.closest('.product__quantity');
      var origMinus = origQtyWrap.querySelector('.js-qty__adjust--minus');
      var origPlus = origQtyWrap.querySelector('.js-qty__adjust--plus');

      var syncQty = function () {
        // defer so the theme's own handlers have updated the value first
        setTimeout(function () {
          stickyQty.value = origQty.value;
        });
      };

      bar.querySelector('[data-sticky-atc-qty-minus]').addEventListener('click', function () {
        origMinus.click();
        syncQty();
      });
      bar.querySelector('[data-sticky-atc-qty-plus]').addEventListener('click', function () {
        origPlus.click();
        syncQty();
      });
      stickyQty.addEventListener('change', function () {
        origQty.value = stickyQty.value;
        origQty.dispatchEvent(new Event('change'));
        syncQty();
      });

      // reflect changes made with the page's own selector
      origQty.addEventListener('change', syncQty);
      origQtyWrap.addEventListener('click', syncQty);
      syncQty();
    }

    /* ---- click behaviour ---- */
    stickyButton.addEventListener('click', function () {
      if (stickyButton.disabled) return;

      if (multiVariant) {
        var target =
          section.querySelector('.variant-input') ||
          section.querySelector('[data-product-blocks]') ||
          origForm;
        var top =
          target.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      } else {
        // Single variant: reuse the real component (MOQ, notifications, count).
        if (origQty) origButton.dataset.qty = origQty.value;
        origButton.click();
      }
    });

    /* ---- visibility: shown from initial page load on mobile ---- */
    var visible = false;

    function setVisible(next) {
      if (!mql.matches) next = false;
      if (next === visible) return;
      visible = next;
      bar.classList.toggle('is-visible', next);
      bar.setAttribute('aria-hidden', next ? 'false' : 'true');
      document.body.classList.toggle('sticky-atc-on', next);
      if (next) document.body.style.setProperty('--sticky-atc-height', bar.offsetHeight + 'px');
      shiftWidgets(next);
    }

    function check() {
      setVisible(true);
    }

    window.addEventListener('pageshow', check);
    check();

    // Floating widgets (chat / cookie banner) often inject after load, so
    // re-run the nudge once they've had a chance to appear.
    window.addEventListener('load', function () {
      setTimeout(function () {
        if (!visible) return;
        shiftWidgets(false);
        shiftWidgets(true);
      }, 1500);
    });

    // Hide + un-shift when leaving mobile, re-evaluate when entering it.
    var onMqChange = function () {
      if (!mql.matches) setVisible(false);
      else check();
    };
    if (mql.addEventListener) mql.addEventListener('change', onMqChange);
    else if (mql.addListener) mql.addListener(onMqChange);

    /* ---- nudge bottom-corner floating widgets out of the way ---- */
    var shifted = [];

    function shiftWidgets(on) {
      if (on) {
        var h = bar.offsetHeight;
        shifted = findCornerWidgets();
        shifted.forEach(function (el) {
          el.setAttribute('data-sticky-atc-prev-transform', el.style.transform || '');
          el.setAttribute('data-sticky-atc-prev-transition', el.style.transition || '');
          el.style.transition = 'transform 0.25s ease';
          el.style.transform =
            (el.style.transform ? el.style.transform + ' ' : '') +
            'translateY(-' + h + 'px)';
        });
      } else {
        shifted.forEach(function (el) {
          el.style.transform = el.getAttribute('data-sticky-atc-prev-transform') || '';
          el.removeAttribute('data-sticky-atc-prev-transform');
          el.removeAttribute('data-sticky-atc-prev-transition');
        });
        shifted = [];
      }
    }

    // Known floating widgets whose fixed node may be nested (e.g. Shopify
    // Inbox renders #shopify-chat > inbox-online-store-chat with the fixed
    // positioning on a descendant, so transforming the wrapper won't move it).
    var KNOWN_WIDGETS = [
      '#shopify-chat',
      'inbox-online-store-chat',
      '#pandectes-gdpr',
      '#pandectes-banner'
    ];

    function isVisibleEl(el, cs) {
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') {
        return false;
      }
      var r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    }

    // Return the actual position:fixed node within a subtree (the element
    // itself or its first fixed descendant in the light DOM).
    function resolveFixedNode(root) {
      if (!root) return null;
      var cs = window.getComputedStyle(root);
      if (cs.position === 'fixed' && isVisibleEl(root, cs)) return root;
      var nodes = root.querySelectorAll('*');
      for (var i = 0; i < nodes.length; i++) {
        var ncs = window.getComputedStyle(nodes[i]);
        if (ncs.position === 'fixed' && isVisibleEl(nodes[i], ncs)) return nodes[i];
      }
      return null;
    }

    // Collect the floating widgets to nudge: explicit known widgets plus any
    // small, fixed, bottom-anchored element injected at the body root.
    function findCornerWidgets() {
      var vh = window.innerHeight;
      var vw = window.innerWidth;
      var seen = [];

      function add(el) {
        if (!el || seen.indexOf(el) !== -1) return;
        if (el === bar || bar.contains(el) || el.hasAttribute('data-sticky-atc')) return;
        seen.push(el);
      }

      // 1) Known widgets — resolve to their real fixed node, skip heuristics.
      KNOWN_WIDGETS.forEach(function (sel) {
        var host = document.querySelector(sel);
        if (host) add(resolveFixedNode(host));
      });

      // 2) Auto-detect: small, fixed, bottom-corner elements at the body root.
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === bar || bar.contains(el) || el.hasAttribute('data-sticky-atc')) return;
        if (el.classList.contains('cc-revoke')) return; // lifted via CSS instead
        var cs = window.getComputedStyle(el);
        if (cs.position !== 'fixed' || !isVisibleEl(el, cs)) return;
        var r = el.getBoundingClientRect();
        if (r.height > vh * 0.6) return; // skip large overlays / open banners
        if (r.width > vw * 0.9) return; // skip full-width bars
        if (r.bottom < vh - 160) return; // must sit near the viewport bottom
        add(el);
      });

      return seen;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
